/**
 * OpenAI Realtime API WebSocket Service
 *
 * Handles real-time voice communication with OpenAI's Realtime API
 * Supports speech-to-speech, function calling, and session management
 */

class OpenAIRealtimeService {
  constructor() {
    this.websocket = null;
    this.isConnected = false;
    this.sessionId = null;
    this.connectionPool = new Map(); // Doctor ID -> Connection info
    this.eventHandlers = new Map();
    this.audioContext = null;
    this.microphone = null;
    this.isRecording = false;
    this.isSpeechActive = false; // Track if user is currently speaking
    this.audioResponseBuffer = []; // Buffer for accumulating audio response deltas
    this.costTracker = null; // Will be imported when needed
    this.costTrackingSession = null;
  }

  /**
   * Get backend proxy URL for voice WebSocket connection
   */
  getBackendProxyUrl() {
    // Use environment variable or default to localhost for development
    const backendHost = import.meta.env.VITE_BACKEND_HOST || "127.0.0.1";
    const backendPort = import.meta.env.VITE_BACKEND_PORT || "8000";
    const backendProtocol = import.meta.env.VITE_BACKEND_PROTOCOL || "ws";

    return `${backendProtocol}://${backendHost}:${backendPort}/ws/voice-proxy`;
  }

  /**
   * Initialize the service with API configuration
   */
  async initialize(config = {}) {
    this.config = {
      apiKey: config.apiKey || process.env.VITE_OPENAI_API_KEY,
      model: config.model || "gpt-4o-realtime-preview",
      voice: config.voice || "marin",
      temperature: config.temperature || 0.8,
      maxTokens: config.maxTokens || 4096,
      ...config,
    };

    if (!this.config.apiKey) {
      throw new Error("OpenAI API key is required for Realtime API");
    }

    // Initialize Web Audio API
    this.audioContext = new (window.AudioContext ||
      window.webkitAudioContext)();

    // Initialize cost tracking
    const { costTracker } = await import("./costTracker.js");
    this.costTracker = costTracker;

    console.log("OpenAI Realtime Service initialized");
  }

  /**
   * Establish WebSocket connection to OpenAI Realtime API
   */
  async connect(doctorId, doctorMemory = []) {
    if (this.isConnected) {
      console.log("Already connected to OpenAI Realtime API");
      return;
    }

    try {
      // Check for existing connection in pool
      const existingConnection = this.connectionPool.get(doctorId);
      if (existingConnection && this.isConnectionValid(existingConnection)) {
        this.websocket = existingConnection.connection;
        this.sessionId = existingConnection.sessionId;
        this.isConnected = true;
        console.log("Reusing existing connection for doctor:", doctorId);
        return;
      }

      console.log("Establishing new WebSocket connection...");

      // OpenAI Realtime API authentication challenge
      // Browser WebSocket API doesn't support custom headers
      // This is a development-only solution - production needs server proxy

      try {
        // Connect to our backend proxy instead of OpenAI directly
        // The backend handles authentication and forwards messages to OpenAI
        const backendProxyUrl = this.getBackendProxyUrl();

        console.log(`🔌 Connecting to backend proxy: ${backendProxyUrl}`);

        this.websocket = new WebSocket(backendProxyUrl);
        this.pendingApiKey = null; // Auth handled in URL

        console.log(
          "✅ Using backend proxy for OpenAI Realtime API authentication"
        );
      } catch (error) {
        console.error("WebSocket creation failed:", error);

        // Provide helpful error message for development
        const helpMessage = `
Backend Proxy Connection Error:

Failed to connect to the backend voice proxy at ${this.getBackendProxyUrl()}.

Please ensure:
1. Backend server is running on the correct port
2. Backend proxy endpoint is accessible
3. CORS is properly configured
4. WebSocket connections are allowed

To start the backend:
npm run backend:dev

For Docker:
npm run backend:docker
        `;

        console.error(helpMessage);
        this.handleConnectionError(
          new Error(
            "Failed to connect to backend proxy. See console for solutions."
          )
        );
        throw error;
      }

      // Set up event listeners
      this.setupWebSocketEvents();

      // Wait for connection to open
      await this.waitForConnection();

      // Initialize session with doctor memory
      await this.initializeSession(doctorMemory);

      // Add to connection pool
      this.connectionPool.set(doctorId, {
        connection: this.websocket,
        sessionId: this.sessionId,
        lastUsed: Date.now(),
        doctorId: doctorId,
      });

      // Start cost tracking session
      if (this.costTracker) {
        this.costTrackingSession = this.costTracker.startSession(doctorId);
      }

      console.log(
        "Connected to OpenAI Realtime API, Session ID:",
        this.sessionId
      );
    } catch (error) {
      console.error("Failed to connect to OpenAI Realtime API:", error);
      throw error;
    }
  }

  /**
   * Set up WebSocket event handlers
   */
  setupWebSocketEvents() {
    this.websocket.onopen = () => {
      console.log("WebSocket connection opened to OpenAI Realtime API");
      this.isConnected = true;
    };

    this.websocket.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        console.log("Received message type:", message.type);
        this.handleMessage(message);
      } catch (error) {
        console.error("Failed to parse WebSocket message:", error);
        this.emit("error", new Error("Invalid message format received"));
      }
    };

    this.websocket.onerror = (error) => {
      console.error("WebSocket error occurred:", error);
      this.emit("error", new Error("WebSocket connection error"));
    };

    this.websocket.onclose = (event) => {
      console.log("WebSocket connection closed:", event.code, event.reason);
      this.isConnected = false;
      this.sessionId = null;
      this.emit("disconnected", event);

      // Handle specific close codes
      if (event.code === 1006) {
        console.error(
          "WebSocket closed abnormally - likely authentication issue"
        );
        this.emit(
          "error",
          new Error("Authentication failed or connection refused")
        );
      } else if (event.code !== 1000) {
        this.scheduleReconnection();
      }
    };
  }

  /**
   * Wait for WebSocket connection to open
   */
  waitForConnection() {
    return new Promise((resolve, reject) => {
      if (this.websocket.readyState === WebSocket.OPEN) {
        resolve();
        return;
      }

      const timeout = setTimeout(() => {
        reject(new Error("Connection timeout"));
      }, 10000);

      this.websocket.addEventListener("open", () => {
        clearTimeout(timeout);
        resolve();
      });

      this.websocket.addEventListener("error", (error) => {
        clearTimeout(timeout);
        reject(error);
      });
    });
  }

  /**
   * Wait for backend proxy session to be established
   */
  async waitForProxySession() {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error("Timeout waiting for proxy session"));
      }, 10000); // 10 second timeout

      // Listen for proxy session created event
      const sessionHandler = (data) => {
        clearTimeout(timeout);
        this.off("sessionCreated", sessionHandler);
        resolve(data);
      };

      this.on("sessionCreated", sessionHandler);
    });
  }

  /**
   * Initialize session with doctor-specific memory and context
   */
  async initializeSession(doctorMemory) {
    // Wait for proxy session to be established
    await this.waitForProxySession();

    // Send session configuration with authentication
    const sessionConfig = {
      type: "session.update",
      session: {
        modalities: ["text", "audio"],
        voice: this.config.voice,
        input_audio_format: "pcm16",
        output_audio_format: "pcm16",
        input_audio_transcription: {
          model: "whisper-1",
        },
        turn_detection: {
          type: "server_vad",
          threshold: 0.5,
          prefix_padding_ms: 300,
          silence_duration_ms: 200,
        },
        tools: this.getAvailableTools(),
        instructions: this.buildSystemInstructions(doctorMemory),
        temperature: this.config.temperature,
        max_response_output_tokens: this.config.maxTokens,
      },
    };

    this.send(sessionConfig);

    // Generate unique session ID
    this.sessionId =
      "session_" + Date.now() + "_" + Math.random().toString(36).substr(2, 9);
  }

  /**
   * Build system instructions incorporating doctor memory
   */
  buildSystemInstructions(doctorMemory) {
    let instructions = `You are an AI medical assistant helping a doctor with patient care and medical documentation. 

Core Capabilities:
- Process voice commands to interact with the medical application
- Understand medical terminology and context
- Execute functions to manipulate the UI (add patients, record vitals, etc.)
- Provide medical insights while maintaining HIPAA compliance
- Talk only in English.

Available Functions: You can call functions to interact with the medical application UI.

Doctor-Specific Context:`;

    // Add doctor memory/rules
    if (doctorMemory && doctorMemory.length > 0) {
      instructions += "\n\nDoctor Preferences and Rules:\n";
      doctorMemory.forEach((memory, index) => {
        instructions += `${index + 1}. ${memory.content}\n`;
      });
    }

    instructions += `\n\nImportant Guidelines:
- Always confirm destructive actions before executing
- Maintain patient privacy and confidentiality
- Be concise but thorough in responses
- Ask for clarification when commands are ambiguous
- Use medical terminology appropriately`;

    return instructions;
  }

  /**
   * Get available tools/functions for the AI to call
   */
  getAvailableTools() {
    return [
      {
        type: "function",
        name: "execute_ui_action",
        description: "Execute a UI action in the medical application",
        parameters: {
          type: "object",
          properties: {
            action: {
              type: "string",
              description:
                "The action to execute (e.g., add-patient, search-patients, export-data)",
              enum: [
                "add-patient",
                "search-patients",
                "view-patients",
                "view-dashboard",
                "export-data",
                "record-vitals",
                "schedule-appointment",
              ],
            },
            parameters: {
              type: "object",
              description: "Optional parameters for the action",
              properties: {
                searchTerm: { type: "string" },
                patientId: { type: "string" },
                value: { type: "string" },
              },
            },
          },
          required: ["action"],
        },
      },
    ];
  }

  /**
   * Handle incoming WebSocket messages
   */
  handleMessage(message) {
    console.log("Received message:", message.type);

    switch (message.type) {
      case "proxy.session.created":
        // Backend proxy has established session
        console.log("Backend proxy session created:", message.data);
        this.sessionId = message.data.connection_id;
        this.emit("sessionCreated", message.data);
        break;

      case "session.created":
        this.emit("sessionCreated", message.session);
        break;

      case "session.updated":
        this.emit("sessionUpdated", message.session);
        break;

      case "input_audio_buffer.speech_started":
        this.emit("speechStarted");
        break;

      case "input_audio_buffer.speech_stopped":
        this.emit("speechStopped");
        break;

      case "response.created":
        this.emit("responseStarted", message.response);
        break;

      case "response.output_item.added":
        if (message.item.type === "audio") {
          this.handleAudioOutput(message.item);
        } else if (message.item.type === "message") {
          this.handleTextResponse(message.item);
        } else if (message.item.type === "function_call") {
          this.handleFunctionCall(message.item);
        }
        break;

      case "response.text.delta":
        this.handleTextDelta(message);
        break;

      case "response.text.done":
        this.handleTextComplete(message);
        break;

      case "response.audio.delta":
        this.handleAudioDelta(message);
        break;

      case "response.audio.done":
        this.handleAudioComplete(message);
        break;

      case "conversation.item.created":
        this.handleConversationItemCreated(message);
        break;

      case "conversation.item.input_audio_transcription.completed":
        this.handleAudioTranscriptionCompleted(message);
        break;

      case "conversation.item.input_audio_transcription.failed":
        console.error("Audio transcription failed:", message);
        break;

      case "input_audio_buffer.committed":
        console.log("Audio buffer committed successfully");
        break;

      case "input_audio_buffer.speech_started":
        console.log("OpenAI detected speech start");
        break;

      case "input_audio_buffer.speech_stopped":
        console.log("OpenAI detected speech stop");
        break;

      case "response.function_call_delta":
        this.handleFunctionCall(message);
        break;

      case "response.done":
        this.emit("responseComplete", message.response);
        break;

      case "error":
        console.error("OpenAI Realtime API error:", message.error);
        console.error("Full error message:", message);
        this.emit("error", message.error);
        break;

      default:
        console.log("Unhandled message type:", message.type);
    }
  }

  /**
   * Handle function calls from the AI
   */
  async handleFunctionCall(message) {
    if (message.name === "execute_ui_action") {
      try {
        const args = JSON.parse(message.arguments || "{}");

        // Import and use the existing agent action service
        const agentActionService = (await import("./agentActions.js")).default;
        const result = agentActionService.execute(args.action, args.parameters);

        // Track function call cost
        if (this.costTracker) {
          this.costTracker.trackFunctionCall();
        }

        // Send function result back to AI
        this.send({
          type: "conversation.item.create",
          item: {
            type: "function_call_output",
            call_id: message.call_id,
            output: JSON.stringify({ success: result, action: args.action }),
          },
        });

        this.emit("functionExecuted", { action: args.action, result });
      } catch (error) {
        console.error("Function call execution error:", error);

        this.send({
          type: "conversation.item.create",
          item: {
            type: "function_call_output",
            call_id: message.call_id,
            output: JSON.stringify({ success: false, error: error.message }),
          },
        });
      }
    }
  }

  /**
   * Handle audio output from the AI
   */
  async handleAudioOutput(audioItem) {
    try {
      if (audioItem.audio && this.audioProcessor) {
        // Convert base64 audio to binary
        const audioData = this.base64ToAudioData(audioItem.audio);

        // Play through audio processor
        await this.audioProcessor.playAudio(audioData);

        console.log("Playing AI audio response:", audioItem.id);
      }

      this.emit("audioReceived", audioItem);
    } catch (error) {
      console.error("Error playing audio output:", error);
      this.emit("error", error);
    }
  }

  /**
   * Stream audio data to OpenAI Realtime API
   */
  streamAudioToOpenAI(audioData) {
    if (!this.isConnected || !this.websocket) {
      return;
    }

    try {
      // Convert Int16Array to base64 for transmission
      const base64Audio = this.audioDataToBase64(audioData);

      const message = {
        type: "input_audio_buffer.append",
        audio: base64Audio,
      };

      this.send(message);

      // Update cost tracking
      const audioDuration =
        audioData.length / (this.config.sampleRate || 16000);
      if (this.costTracker) {
        this.costTracker.trackAudioInput(audioDuration);
      }
    } catch (error) {
      console.error("Error streaming audio to OpenAI:", error);
    }
  }

  /**
   * Handle voice activity detection events
   */
  handleVoiceActivity(vadData) {
    const { type, level, isSpeaking } = vadData;

    switch (type) {
      case "speechStart":
        console.log("Speech started, level:", level);
        this.emit("speechStarted", { level });
        break;

      case "speechEnd":
        console.log("Speech ended, level:", level);
        this.emit("speechStopped", { level });

        // Trigger response generation when speech ends
        if (this.isConnected) {
          this.send({ type: "input_audio_buffer.commit" });
          this.send({ type: "response.create" });
        }
        break;

      case "levelUpdate":
        this.emit("voiceLevelUpdate", { level, isSpeaking });
        break;
    }
  }

  /**
   * Convert Int16Array audio data to base64
   */
  audioDataToBase64(audioData) {
    // Convert Int16Array to Uint8Array
    const uint8Array = new Uint8Array(audioData.buffer);

    // Convert to base64
    let binary = "";
    for (let i = 0; i < uint8Array.length; i++) {
      binary += String.fromCharCode(uint8Array[i]);
    }

    return btoa(binary);
  }

  /**
   * Convert base64 audio to Int16Array
   */
  base64ToAudioData(base64Audio) {
    // Decode base64
    const binary = atob(base64Audio);
    const uint8Array = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i++) {
      uint8Array[i] = binary.charCodeAt(i);
    }

    // Convert to Int16Array
    return new Int16Array(uint8Array.buffer);
  }

  /**
   * Start recording audio from microphone
   */
  async startRecording() {
    if (this.isRecording) return;

    try {
      // Import and initialize audio processor
      const { audioProcessor } = await import("./audioProcessor.js");

      // Set up audio callbacks
      audioProcessor.onAudioData = (audioData) => {
        this.streamAudioToOpenAI(audioData);
      };

      audioProcessor.onVoiceActivity = (vadData) => {
        this.handleVoiceActivity(vadData);
      };

      audioProcessor.onError = (error) => {
        console.error("Audio processor error:", error);
        this.emit("error", error);
      };

      // Start recording
      const success = await audioProcessor.startRecording();
      if (!success) {
        throw new Error("Failed to start audio processor");
      }

      this.audioProcessor = audioProcessor;
      this.isRecording = true;
      this.emit("recordingStarted");

      console.log("Started audio recording with OpenAI streaming");
    } catch (error) {
      console.error("Failed to start recording:", error);
      this.emit("error", error);
    }
  }

  /**
   * Stop recording audio
   */
  stopRecording() {
    if (!this.isRecording) return;

    if (this.audioProcessor) {
      this.audioProcessor.stopRecording();
      this.audioProcessor.onAudioData = null;
      this.audioProcessor.onVoiceActivity = null;
      this.audioProcessor.onError = null;
      this.audioProcessor = null;
    }

    this.isRecording = false;
    this.emit("recordingStopped");

    console.log("Stopped audio recording");
  }

  /**
   * Stream audio data to OpenAI Realtime API
   */
  streamAudioToOpenAI(audioData) {
    if (!this.isConnected || !audioData) {
      console.log(
        "OpenAI Service: Cannot stream audio - connection:",
        this.isConnected,
        "audioData:",
        !!audioData
      );
      return;
    }

    try {
      console.log(
        "OpenAI Service: Streaming audio data, length:",
        audioData.length
      );

      // Convert Int16Array to base64 for OpenAI Realtime API
      const audioBase64 = this.audioDataToBase64(audioData);
      console.log(
        "OpenAI Service: Converted to base64, length:",
        audioBase64.length
      );

      // Send audio buffer append message
      const message = {
        type: "input_audio_buffer.append",
        audio: audioBase64,
      };

      const success = this.send(message);
      console.log("OpenAI Service: Sent audio message, success:", success);
    } catch (error) {
      console.error("Failed to stream audio to OpenAI:", error);
    }
  }

  /**
   * Convert audio data (Int16Array) to base64 string
   */
  audioDataToBase64(audioData) {
    // Convert Int16Array to Uint8Array
    const uint8Array = new Uint8Array(audioData.buffer);

    // Convert to base64
    let binary = "";
    for (let i = 0; i < uint8Array.length; i++) {
      binary += String.fromCharCode(uint8Array[i]);
    }

    return btoa(binary);
  }

  /**
   * Handle voice activity detection events
   */
  handleVoiceActivity(vadData) {
    const { type, level, isSpeaking, speechDuration } = vadData;

    // Emit voice activity events for UI feedback
    this.emit("voiceActivity", vadData);

    // Only process actual speech events (not null/undefined)
    if (type === "speechStart") {
      console.log("Speech started, level:", level.toFixed(4));
      this.isSpeechActive = true;
    } else if (type === "speechEnd") {
      console.log(
        "Speech ended, level:",
        level.toFixed(4),
        "duration:",
        speechDuration + "ms"
      );

      // When speech ends, commit the audio buffer and request response
      if (this.isSpeechActive) {
        this.isSpeechActive = false;
        console.log("Triggering audio commit and response request...");
        this.commitAudioAndRequestResponse();
      }
    }

    // Update voice level for visualization (always)
    this.emit("voiceLevel", { level, isSpeaking });
  }

  /**
   * Commit audio buffer and request OpenAI response
   */
  commitAudioAndRequestResponse() {
    console.log("Committing audio buffer and requesting response...");

    // Commit the audio buffer
    const commitMessage = {
      type: "input_audio_buffer.commit",
    };
    this.send(commitMessage);

    // Request response generation with explicit text output
    const responseMessage = {
      type: "response.create",
      response: {
        modalities: ["text", "audio"],
        instructions:
          "You are a medical assistant. Listen to the voice command and transcribe it exactly as spoken. Then respond with the appropriate action. If the user says commands like 'add patient', 'view patients', 'view dashboard', etc., respond with a function call to execute that action.",
        output_audio_format: "pcm16",
        voice: this.config.voice || "marin",
        temperature: 0.6,
        max_output_tokens: 4096,
      },
    };
    this.send(responseMessage);
  }

  /**
   * Send message to OpenAI Realtime API
   */
  send(message) {
    if (!this.isConnected || !this.websocket) {
      console.error("Cannot send message: not connected");
      return false;
    }

    try {
      // Send message directly - authentication is handled at connection level
      this.websocket.send(JSON.stringify(message));
      console.log("Sent message type:", message.type);
      return true;
    } catch (error) {
      console.error("Failed to send message:", error);
      return false;
    }
  }

  /**
   * Handle text response from OpenAI
   */
  handleTextResponse(item) {
    console.log("Received text response:", item);
    this.emit("textResponse", item);
  }

  /**
   * Handle text delta (streaming text)
   */
  handleTextDelta(message) {
    console.log("Text delta:", message.delta);
    this.emit("textDelta", message);
  }

  /**
   * Handle completed text response
   */
  handleTextComplete(message) {
    console.log("Text response complete:", message.text);

    // Process the text for voice commands
    this.processVoiceCommand(message.text);

    this.emit("textComplete", message);
  }

  /**
   * Handle audio delta (streaming audio)
   */
  handleAudioDelta(message) {
    console.log(
      "Audio delta received, length:",
      message.delta ? message.delta.length : 0
    );

    // Accumulate audio deltas for playback
    if (message.delta) {
      if (!this.audioResponseBuffer) {
        this.audioResponseBuffer = [];
      }
      this.audioResponseBuffer.push(message.delta);
      console.log(
        "🔊 Accumulated audio buffer size:",
        this.audioResponseBuffer.length
      );
    }

    this.emit("audioDelta", message);
  }

  /**
   * Handle completed audio response
   */
  async handleAudioComplete(message) {
    console.log("Audio response complete", message);

    // Play accumulated audio buffer
    if (this.audioResponseBuffer && this.audioResponseBuffer.length > 0) {
      try {
        console.log("🔊 Playing complete audio response...");

        // Concatenate all audio deltas
        const completeAudio = this.audioResponseBuffer.join("");
        await this.handleAudioPlayback(completeAudio);

        // Clear buffer for next response
        this.audioResponseBuffer = [];
      } catch (error) {
        console.error("❌ Failed to play complete audio:", error);
      }
    }

    this.emit("audioComplete", message);
  }

  /**
   * Handle conversation item created
   */
  handleConversationItemCreated(message) {
    console.log("Conversation item created:", message.item);
    console.log("Item content:", JSON.stringify(message.item.content, null, 2));

    if (message.item.type === "message") {
      const content = message.item.content;
      if (content && content.length > 0) {
        for (const contentItem of content) {
          console.log("Processing content item:", contentItem);

          // Handle different content types
          if (contentItem.type === "input_audio" && contentItem.transcript) {
            console.log(
              "🎤 Found audio transcription:",
              contentItem.transcript
            );
            this.processVoiceCommand(contentItem.transcript);
          } else if (contentItem.type === "text" && contentItem.text) {
            console.log("📝 Found text content:", contentItem.text);
            if (message.item.role === "user") {
              this.processVoiceCommand(contentItem.text);
            } else if (message.item.role === "assistant") {
              console.log("🤖 Assistant response:", contentItem.text);
            }
          } else if (contentItem.type === "audio" && contentItem.audio) {
            console.log(
              "🔊 Found audio content, length:",
              contentItem.audio.length
            );
            this.handleAudioPlayback(contentItem.audio);
          }
        }
      }
    }
  }

  /**
   * Handle audio transcription completed
   */
  handleAudioTranscriptionCompleted(message) {
    console.log("Audio transcription completed:", message);

    if (message.transcript) {
      console.log("Transcription result:", message.transcript);
      this.processVoiceCommand(message.transcript);
    }
  }

  /**
   * Handle audio playback from OpenAI response
   */
  async handleAudioPlayback(audioData) {
    try {
      console.log("🔊 Attempting to play audio response...");

      // Import audio processor for playback
      const { audioProcessor } = await import("./audioProcessor.js");

      // Convert base64 audio to audio buffer and play
      const audioBuffer = this.base64ToAudioData(audioData);
      await audioProcessor.playAudio(audioBuffer);

      console.log("✅ Audio playback started");
      this.emit("audioReceived");
    } catch (error) {
      console.error("❌ Failed to play audio:", error);
      this.emit("error", new Error(`Audio playback failed: ${error.message}`));
    }
  }

  /**
   * Handle function call from OpenAI
   */
  async handleFunctionCall(item) {
    console.log("Function call received:", item);

    try {
      const functionName = item.name;
      const functionArgs = item.arguments ? JSON.parse(item.arguments) : {};

      console.log(`Executing function: ${functionName}`, functionArgs);

      // Import and execute the action
      const agentActionService = (await import("./agentActions.js")).default;
      const result = await agentActionService.execute(
        functionName,
        functionArgs
      );

      console.log("Function execution result:", result);
      this.emit("functionCall", {
        name: functionName,
        args: functionArgs,
        result,
      });
    } catch (error) {
      console.error("Error executing function call:", error);
      this.emit("error", error);
    }
  }

  /**
   * Process voice command text and execute actions
   */
  async processVoiceCommand(text) {
    if (!text) return;

    console.log("Processing voice command:", text);

    try {
      // Import database and agent actions
      const { medicalDB } = await import("./database.js");
      const agentActionService = (await import("./agentActions.js")).default;

      // Get current doctor ID (you might want to make this configurable)
      const doctorId = "doctor_1"; // Default for now

      // Find matching voice commands
      const matchingCommands = await medicalDB.findMatchingVoiceCommands(
        doctorId,
        text
      );

      if (matchingCommands.length > 0) {
        const command = matchingCommands[0]; // Use the first match
        console.log("Matched voice command:", command.name);

        // Execute the command actions
        for (const action of command.actions) {
          console.log("Executing action:", action);
          await agentActionService.execute(action, command.parameters);
        }

        // Update usage count
        await medicalDB.incrementVoiceCommandUsage(command.id);

        this.emit("commandExecuted", { command, text });
      } else {
        console.log("No matching voice command found for:", text);
        this.emit("commandNotFound", { text });
      }
    } catch (error) {
      console.error("Error processing voice command:", error);
      this.emit("error", error);
    }
  }

  /**
   * Send text input to the AI
   */
  sendText(text) {
    const message = {
      type: "conversation.item.create",
      item: {
        type: "message",
        role: "user",
        content: [
          {
            type: "input_text",
            text: text,
          },
        ],
      },
    };

    this.send(message);
    this.send({ type: "response.create" });
  }

  /**
   * Check if connection is still valid
   */
  isConnectionValid(connectionInfo) {
    const maxAge = 30 * 60 * 1000; // 30 minutes
    const age = Date.now() - connectionInfo.lastUsed;

    return (
      age < maxAge && connectionInfo.connection.readyState === WebSocket.OPEN
    );
  }

  /**
   * Schedule reconnection attempt
   */
  scheduleReconnection() {
    setTimeout(() => {
      if (!this.isConnected) {
        console.log("Attempting to reconnect...");
        this.emit("reconnecting");
        // Note: Reconnection logic would need doctor ID and memory
        // This should be handled by the calling component
      }
    }, 5000);
  }

  /**
   * Disconnect from OpenAI Realtime API
   */
  disconnect() {
    if (this.websocket) {
      this.websocket.close(1000, "User disconnected");
    }

    this.stopRecording();

    // End cost tracking session
    if (this.costTracker && this.costTrackingSession) {
      this.costTracker.endSession();
      this.costTrackingSession = null;
    }

    this.isConnected = false;
    this.sessionId = null;

    console.log("Disconnected from OpenAI Realtime API");
  }

  /**
   * Get cost tracking information
   */
  getCostInfo() {
    if (!this.costTracker) {
      return {
        costs: { session: { current: 0 }, daily: { current: 0 } },
        usage: { currentSession: { duration: 0 } },
      };
    }

    return {
      costs: this.costTracker.getCurrentCosts(),
      usage: this.costTracker.getUsageStats(),
    };
  }

  /**
   * Event system for handling real-time updates
   */
  on(event, handler) {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, []);
    }
    this.eventHandlers.get(event).push(handler);
  }

  off(event, handler) {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  emit(event, data) {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach((handler) => handler(data));
    }
  }

  /**
   * Clean up connection pool
   */
  cleanupConnectionPool() {
    const now = Date.now();
    const maxAge = 30 * 60 * 1000; // 30 minutes

    for (const [doctorId, connectionInfo] of this.connectionPool) {
      if (
        now - connectionInfo.lastUsed > maxAge ||
        connectionInfo.connection.readyState !== WebSocket.OPEN
      ) {
        connectionInfo.connection.close();
        this.connectionPool.delete(doctorId);
        console.log("Cleaned up stale connection for doctor:", doctorId);
      }
    }
  }
}

// Create singleton instance
const openaiRealtimeService = new OpenAIRealtimeService();

export { openaiRealtimeService, OpenAIRealtimeService };
